import { useEffect, useState } from "react";
import { fetchCalendarEvents, type GraphCalendarEvent } from "../../email/services/graph";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";

interface UseCalendarEventsResult {
  events: GraphCalendarEvent[];
  loading: boolean;
  error: string | null;
  conflictIndices: Set<number>;
}

function detectConflicts(events: GraphCalendarEvent[]): Set<number> {
  const conflicts = new Set<number>();
  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      const a = events[i]!;
      const b = events[j]!;
      const aStart = new Date(a.start.dateTime + "Z");
      const aEnd = new Date(a.end.dateTime + "Z");
      const bStart = new Date(b.start.dateTime + "Z");
      const bEnd = new Date(b.end.dateTime + "Z");
      if (aStart < bEnd && bStart < aEnd) {
        conflicts.add(i);
        conflicts.add(j);
      }
    }
  }
  return conflicts;
}

export function useCalendarEvents(token: string | null): UseCalendarEventsResult {
  const { refreshGraphToken } = useAuthState();
  const [events, setEvents] = useState<GraphCalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setEvents([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const today = new Date();
    const start = new Date(today);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);

    fetchCalendarEvents(token, start.toISOString(), end.toISOString(), refreshGraphToken)
      .then((result) => {
        if (cancelled) return;
        setEvents(result);
        logConnectorSuccess("calendar", "load-events");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load calendar";
        setError(msg);
        logConnectorFailure("calendar", e, "load-events");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, refreshGraphToken]);

  return { events, loading, error, conflictIndices: detectConflicts(events) };
}
